import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, ChevronLeft, ShoppingBag, ShoppingCart } from "lucide-react";
import { useI18n } from "@/i18n";
import { useCartToken } from "./ShopCart";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const shopApi = axios.create({ baseURL: `${BACKEND}/api/public/shop` });
const img = (url) => (url ? `${BACKEND}${url}` : null);

export default function StorefrontProduct() {
  const { slug, productId } = useParams();
  const [sp] = useSearchParams();
  const { t, locale, setLocale } = useI18n();
  const qlang = sp.get("lang");
  const [p, setP] = useState(null);
  const [state, setState] = useState("loading");
  const nav = useNavigate();
  const { ensure } = useCartToken(slug);
  const [adding, setAdding] = useState(false);
  const addToCart = async () => {
    setAdding(true);
    try {
      const tk = await ensure();
      await shopApi.post(`/cart/${tk}/items`, { product_id: productId, qty: 1 });
      nav(`/shop/${slug}/cart`);
    } catch { alert(t("shop.storefront.error")); } finally { setAdding(false); }
  };

  useEffect(() => { if (qlang && qlang !== locale) setLocale(qlang); }, [qlang, locale, setLocale]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await shopApi.get(`/${slug}/products/${productId}`, { params: { lang: qlang || locale } });
        if (alive) { setP(data); setState("ready"); }
      } catch { if (alive) setState("error"); }
    })();
    return () => { alive = false; };
  }, [slug, productId, qlang, locale]);

  if (state === "loading") return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (state === "error") return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 p-6 text-center">
      <p className="text-slate-500">{t("shop.storefront.error")}</p>
      <Link to={`/shop/${slug}`} className="text-sm font-medium text-blue-600">{t("shop.storefront.back")}</Link>
    </div>
  );

  const avail = p.availability?.state;
  const availLabel = { in_stock: t("shop.storefront.in_stock"), backorder: t("shop.storefront.backorder"), out_of_stock: t("shop.storefront.out_of_stock") }[avail] || "";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sf-product-detail">
      <div className="mx-auto max-w-5xl px-4 py-6">
        <Link to={`/shop/${slug}?lang=${locale}`} data-testid="sf-back" className="mb-6 inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-800">
          <ChevronLeft className="h-4 w-4" />{t("shop.storefront.back")}
        </Link>
        <div className="grid gap-8 lg:grid-cols-2">
          <div className="flex aspect-square items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white">
            {p.image_url ? <img src={img(p.image_doc_url || p.image_url)} alt={p.name} className="h-full w-full object-contain" />
              : <ShoppingBag className="h-24 w-24 text-slate-200" />}
          </div>
          <div className="flex flex-col gap-4">
            {p.manufacturer && <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{p.manufacturer}</span>}
            <h1 className="text-2xl font-bold text-slate-900" data-testid="sf-detail-name">{p.name}</h1>
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-bold text-slate-900" data-testid="sf-detail-price">€ {p.price_gross}</span>
              <span className="text-sm text-slate-400">{t("shop.storefront.incl_vat")} ({p.vat_rate}% {t("shop.storefront.vat")})</span>
            </div>
            <div className="text-sm text-slate-500">{t("shop.storefront.excl_vat")}: € {p.price_net}</div>
            <span className={`w-fit rounded-full px-3 py-1 text-xs font-medium ${avail === "in_stock" ? "bg-emerald-100 text-emerald-700" : avail === "backorder" ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-600"}`}>{availLabel}</span>
            <button onClick={addToCart} disabled={adding || avail === "out_of_stock"} data-testid="sf-add-cart"
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-3 font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-8">
              {adding ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShoppingCart className="h-5 w-5" />}{t("shop.storefront.add_to_cart")}
            </button>
            <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 border-t border-slate-200 pt-4 text-sm">
              <dt className="text-slate-400">{t("shop.storefront.sku")}</dt><dd className="text-slate-700">{p.sku}</dd>
              {p.ean_gtin && (<><dt className="text-slate-400">EAN</dt><dd className="text-slate-700">{p.ean_gtin}</dd></>)}
              <dt className="text-slate-400">{t("shop.storefront.unit")}</dt><dd className="text-slate-700">{p.unit}</dd>
            </dl>
          </div>
        </div>
        {p.description && (
          <div className="mt-10 max-w-3xl">
            <h2 className="mb-2 text-lg font-semibold text-slate-900">{t("shop.storefront.details")}</h2>
            <p className="whitespace-pre-line text-sm leading-relaxed text-slate-600" data-testid="sf-detail-desc">{p.description}</p>
          </div>
        )}
        {(p.related || []).length > 0 && (
          <div className="mt-12">
            <h2 className="mb-4 text-lg font-semibold text-slate-900">{t("shop.storefront.related")}</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {p.related.map((r) => (
                <Link key={r.id} to={`/shop/${slug}/p/${r.id}?lang=${locale}`} className="rounded-xl border border-slate-200 bg-white p-3 hover:shadow">
                  <div className="mb-2 flex aspect-square items-center justify-center overflow-hidden rounded bg-slate-50">
                    {r.image_url ? <img src={img(r.image_url)} alt={r.name} className="h-full w-full object-cover" /> : <ShoppingBag className="h-10 w-10 text-slate-300" />}
                  </div>
                  <div className="line-clamp-2 text-xs font-medium text-slate-700">{r.name}</div>
                  <div className="mt-1 text-sm font-bold text-slate-900">€ {r.price_gross}</div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
